import { z } from 'zod';
import { useMemo } from 'react';
import { format, parse } from 'date-fns';
import { EmployeeFormType, NameByValue, SelectItemType } from '@/types';
import { EmployeeLocation } from '@/api/types';
import { OrganType } from '@/api/apis/case';
import { AffiliationType, Employee, EmployeeRole, LocationPreference, ScrubSize } from '@/api/apis/resources';
import { Hub } from '@/api/apis/facilities';
import { getNameValuesFromEnumNames, STATE_SCHEMA } from '@/utils/helpers';
import { EMPLOYEE_ROLE_NAMES } from '@/constants/user.constants';
import { TMDXFile } from '@/api/apis/files';
import { contractorCertificationSchema } from '../ContractorForm';
import { createEmployeeFormValidation } from './utils';
import { CCResourceType, CSHolidayDutyGroup, CSShiftTimeGroup } from '@/api/apis/resources/schedule';
import { useCountriesData } from '@/contexts/CountriesDataProvider';

export const EMPLOYEE_ROLES_SELECT_LIST = getNameValuesFromEnumNames(EMPLOYEE_ROLE_NAMES);
export const EMPLOYEE_ORGAN_LIST = getNameValuesFromEnumNames(OrganType);
export const DEFAULT_OMS_ORGAN_SPECIALIZATIONS: OrganType[] = [OrganType.Heart, OrganType.Liver, OrganType.Lungs];

export const getDefaultValues = (
  data: Employee,
  homeLocation?: EmployeeLocation,
  baseLocation?: Hub | null,
): EmployeeFormType => {
  return {
    firstName: data.firstName,
    lastName: data.lastName,
    profilePhoto: data.profilePhoto || '',
    roles: data.roles.filter((r) => Object.values(EmployeeRole).includes(r)),
    organType: data.organSpecializations
      ? data.organSpecializations.filter((r) => Object.values(OrganType).includes(r))
      : [],
    email: data.email,
    phone: data.phone || '',
    hireDate: data.hireDate ? format(parse(data.hireDate, 'yyyy-MM-dd', new Date()), 'MM / dd / yyyy') : '',
    position: data.position || '',
    dateOfBirth: data.dateOfBirth ? format(parse(data.dateOfBirth, 'yyyy-MM-dd', new Date()), "MMM do',' yyyy") : '',
    tsaPrecheckNumber: data.tsaPrecheckNumber,
    isActive: data.isActive,
    scrubSizeTop: data.scrubSizeTop,
    scrubSizeBottom: data.scrubSizeBottom,
    weight: data.weight,
    locationPreference: data.locationPreference,
    closestAirportForDeploymentId: data?.closestAirportForDeployment?.toString(),
    canWorkOnExtraShifts: data.canWorkOnExtraShifts,
    extraShiftsCount: data.extraShiftsCount,
    canTravel: data.canTravel,
    travelShiftsCount: data.travelShiftsCount,
    clinicalSpecialistShiftGroupType: data.clinicalSpecialistShiftGroupType,
    shiftTimeGroup: data.shiftTimeGroup,
    liverSurgeonRegionId: data.liverSurgeonRegionId,
    heartLungsSurgeonRegionId: data.heartLungsSurgeonRegionId,
    clinicalCoordinatorType: data.clinicalCoordinatorType,
    phoneSupportOrganSpecializations: data.phoneSupportOrganSpecializations,
    rsScheduleManagerOrgans: data.rsScheduleManagerOrgans,
    organManagementSpecialistSpecializations: data.organManagementSpecialistSpecializations,
    shadowOrganSpecializations: data.shadowOrganSpecializations ?? [],
    proctorOrganSpecializations: data.proctorOrganSpecializations ?? [],
    trialProgramIds: data.trialPrograms?.map((tp) => tp.id) || null,
    addressId: homeLocation?.id || '',
    addressLine: homeLocation?.addressLine || '',
    city: homeLocation?.city || '',
    state: homeLocation?.state || null,
    postalCode: homeLocation?.postalCode || '',
    country: homeLocation?.country || '',
    currentLocation: data.currentLocation,
    baseLocationId: baseLocation?.id || null,
    longitude: homeLocation?.longitude ?? 0,
    latitude: homeLocation?.latitude ?? 0,
    affiliationType: data?.affiliation?.affiliationType || null,
    affiliationTxCenterId: data.affiliation?.txCenterId || null,
    affiliationOpoId: data.affiliation?.opoId || null,
    contractorCertifications: data.contractorCertifications || null,
    homeLocationTimezone: homeLocation?.timezone || '',
  };
};

export const employeeFormSchema = (composedSubdivisionsList: Record<string, NameByValue[]>) =>
  z
    .object({
      firstName: z.string(),
      lastName: z.string(),
      profilePhoto: z.string().optional(),
      image: z.custom<TMDXFile>().optional(),
      dateOfBirth: z.string(),
      roles: z.enum(EmployeeRole, { error: 'Invalid Role' }).array().nonempty('Provide role'),
      organType: z.array(z.enum(OrganType)),
      email: z.string(),
      phone: z.string(),
      hireDate: z.string({ error: 'Select hire date' }),
      position: z.string().optional(),
      tsaPrecheckNumber: z.string().nullable(),
      isActive: z.boolean(),
      scrubSizeTop: z.enum(ScrubSize).nullable(),
      scrubSizeBottom: z.enum(ScrubSize).nullable(),
      weight: z.number().positive('Provide weight').nullable(),
      locationPreference: z.enum(LocationPreference).optional(),
      closestAirportForDeploymentId: z.string().optional(),
      canWorkOnExtraShifts: z.boolean(),
      extraShiftsCount: z.number().min(0).max(30),
      canTravel: z.boolean(),
      travelShiftsCount: z.number().min(0).max(30),
      clinicalSpecialistShiftGroupType: z.enum(CSHolidayDutyGroup).nullable(),
      shiftTimeGroup: z.enum(CSShiftTimeGroup).nullable(),
      liverSurgeonRegionId: z.string().nullable(),
      heartLungsSurgeonRegionId: z.string().nullable(),
      clinicalCoordinatorType: z.enum(CCResourceType).nullable(),
      phoneSupportOrganSpecializations: z.array(z.enum(OrganType)),
      rsScheduleManagerOrgans: z.array(z.enum(OrganType)),
      organManagementSpecialistSpecializations: z.array(z.enum(OrganType)),
      shadowOrganSpecializations: z.array(z.enum(OrganType)),
      proctorOrganSpecializations: z.array(z.enum(OrganType)),
      trialProgramIds: z.array(z.string()).nullable(),
      addressId: z.string().min(1, 'Provide address'),
      addressLine: z.string().min(1, 'Provide address'),
      city: z.string().min(1, 'Provide city'),
      state: STATE_SCHEMA,
      postalCode: z.string().min(1, 'Provide postal code'),
      country: z.string(),
      currentLocation: z.custom<EmployeeLocation>(),
      baseLocationId: z.string().min(1).nullable(),
      latitude: z.number().min(-90).max(90),
      longitude: z.number().min(-180).max(180),
      affiliationType: z.enum(AffiliationType).nullable(),
      affiliationTxCenterId: z.string().nullable(),
      affiliationOpoId: z.string().nullable(),
      contractorCertifications: z.array(contractorCertificationSchema).nullable(),
      homeLocationTimezone: z.string(),
    })
    .superRefine(createEmployeeFormValidation(composedSubdivisionsList));

export const useEmployeeFormSchema = () => {
  const { composedSubdivisionsList, isLoading } = useCountriesData();
  const schema = useMemo(() => employeeFormSchema(composedSubdivisionsList), [composedSubdivisionsList]);
  return { schema, isLoading };
};

export const CS_GROUPS: SelectItemType[] = [
  { value: 'GroupA', name: 'Group A' },
  { value: 'GroupB', name: 'Group B' },
  { value: 'GroupC', name: 'Group C' },
];

export const SHIFT_TIME_GROUPS: SelectItemType[] = [
  { value: CSShiftTimeGroup.GroupA, name: 'Group A (11:00 - 10:59)' },
  { value: CSShiftTimeGroup.GroupB, name: 'Group B (15:00 - 14:59)' },
];

export const TC_LEVEL: SelectItemType[] = [
  { value: 'TC1', name: 'TCI' },
  { value: 'TC2', name: 'TCII' },
  { value: 'TC3', name: 'TCIII' },
];

export const DISABLED_KEYS_BY_ROLES: Partial<Record<EmployeeRole, EmployeeRole[]>> = {
  [EmployeeRole.SuperAdmin]: [],
  [EmployeeRole.Admin]: [
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.Section16Officer,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.Admin,
    EmployeeRole.CLevel,
  ],
  [EmployeeRole.TechSupportAdmin]: [
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.Section16Officer,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.Admin,
    EmployeeRole.CLevel,
  ],
  [EmployeeRole.ClinicalCoordinatorManagement]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.CLevel,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsManager,
    EmployeeRole.AviationSpecialist,
    EmployeeRole.GroundSpecialist,
    EmployeeRole.OrganLeadership,
    EmployeeRole.OrganManagement,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.ClinicalAuditor,
    EmployeeRole.ClinicalAffairsLeadership,
    EmployeeRole.ScheduleManagerCs,
    EmployeeRole.ScheduleManagerRs,
    EmployeeRole.ScheduleManagerCc,
    EmployeeRole.RegularUser,
    EmployeeRole.FinanceAuditor,
    EmployeeRole.MaintenanceOfficer,
    EmployeeRole.Section16Officer,
    EmployeeRole.NationalCSManagement,
    EmployeeRole.CommercialAdoptionManagement,
  ],
};
